import React, { createContext, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/shared/lib/cn";
import { drawBuddy, type Part } from "./buddy";
import type { LpcAvatarProps } from "./LpcAvatar";

// Read as LpcAvatar does: no framer-motion here, since the Chrome extension renders this file too.
const stillness = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export interface AgentAvatarStatus { running?: boolean; unpublished?: boolean }

/** Keyed by saved seed. Only the workspace shell provides it, so the client app and shared
    chats never show a draft's amber. A known `running` wins over the `animate` prop. */
export const AgentAvatarState = createContext<ReadonlyMap<string, AgentAvatarStatus>>(new Map());

const PAINT: Record<Exclude<Part["paint"], "ink">, React.SVGProps<SVGPathElement>> = {
  hole: { fill: "var(--surface)" },
  dot: { fill: "currentColor" },
  blush: { fill: "var(--surface)", opacity: 0.45 },
};

/* An agent's ink buddy (buddy.ts) on a round chip of the surface colour, so its eyes and mouth
   read as cut out of the ink. Amber while the agent has unpublished changes.

   While it works the frames loop. When it stops it finishes the loop and rests: a buddy frozen
   mid-hop or mid-blink looks broken. The frames are only in the DOM while it moves, since a
   page shows dozens of avatars. Reduced motion keeps it at rest. */
export default function BuddyAvatar({ seed = "agent", size = 24, animate, ring, className }: LpcAvatarProps) {
  const filter = `buddy-${useId().replace(/:/g, "")}`;
  const { parts, seconds } = useMemo(() => drawBuddy(seed), [seed]);
  const state = useContext(AgentAvatarState).get(seed);
  const running = !stillness() && (state?.running ?? animate === "run");
  const [moving, setMoving] = useState(running);
  const svg = useRef<SVGSVGElement>(null);
  const began = useRef(0);

  useEffect(() => {
    if (running) {
      setMoving(true);
      return;
    }
    const elapsed = (svg.current?.getCurrentTime?.() ?? began.current) - began.current;
    const timer = window.setTimeout(() => setMoving(false), ((seconds - (elapsed % seconds)) % seconds) * 1000);
    return () => window.clearTimeout(timer);
  }, [running, seconds]);

  // Every loop starts from the resting frame, whenever the avatar was drawn.
  useLayoutEffect(() => {
    if (!moving || !svg.current?.getCurrentTime) return;
    began.current = svg.current.getCurrentTime();
    svg.current.querySelectorAll("animate").forEach((frames) => frames.beginElement());
  }, [moving]);

  const path = (part: Part, paint: React.SVGProps<SVGPathElement>, key: number) => (
    <path key={key} d={part.frames[0]} {...paint}>
      {moving && <animate attributeName="d" values={[...part.frames, part.frames[0]].join(";")} dur={`${seconds}s`} begin="indefinite" repeatCount="indefinite" />}
    </path>
  );

  return (
    <span aria-hidden="true" className={cn("inline-flex shrink-0 rounded-full bg-[var(--surface)]", ring && "ring-1 ring-[var(--hairline)]",
      state?.unpublished ? "text-[var(--warn)]" : "text-[var(--text-primary)]", className)} style={{ width: size, height: size }}>
      <svg ref={svg} viewBox="-50 -50 100 100" focusable="false" className="h-full w-full">
        <defs>
          {/* Blur the drops, then cut the alpha sharply: neighbours pool into one body, as ink does. */}
          <filter id={filter} filterUnits="userSpaceOnUse" x="-50" y="-50" width="100" height="100">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3.2" result="soft" />
            <feColorMatrix in="soft" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -9" />
          </filter>
        </defs>
        {parts.map((part, i) => part.paint === "ink"
          ? <g key={i} filter={`url(#${filter})`}>{path(part, { fill: "currentColor" }, i)}</g>
          : path(part, PAINT[part.paint], i))}
      </svg>
    </span>
  );
}
